import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const RandomNumberGeneratorPage = () => {
  const [min, setMin] = useState('1');
  const [max, setMax] = useState('100');
  const [quantity, setQuantity] = useState('1');
  const [allowDuplicates, setAllowDuplicates] = useState(true);
  const [results, setResults] = useState([]);
  const [sortResults, setSortResults] = useState(false);

  const generate = () => {
    const minVal = parseInt(min) || 0;
    const maxVal = parseInt(max) || 100;
    const qty = Math.min(parseInt(quantity) || 1, allowDuplicates ? 1000 : maxVal - minVal + 1);
    
    let nums = [];
    if (allowDuplicates) {
      for (let i = 0; i < qty; i++) {
        nums.push(Math.floor(Math.random() * (maxVal - minVal + 1)) + minVal);
      }
    } else {
      const available = [];
      for (let i = minVal; i <= maxVal; i++) available.push(i);
      for (let i = 0; i < qty && available.length > 0; i++) {
        const idx = Math.floor(Math.random() * available.length);
        nums.push(available.splice(idx, 1)[0]);
      }
    }

    if (sortResults) nums.sort((a, b) => a - b);
    setResults(nums);
  };

  const copyResults = () => {
    navigator.clipboard.writeText(results.join(', '));
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/math" className="text-cyan-400 hover:text-cyan-300">Math</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Random Number Generator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Random Number Generator</h1>
          <p className="text-gray-400 mb-6">
            Generate random numbers within a specified range. Perfect for lotteries, games, statistical sampling, and more.
          </p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 font-medium w-24">Min</label>
                  <Input type="number" value={min} onChange={(e) => setMin(e.target.value)}
                    className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 font-medium w-24">Max</label>
                  <Input type="number" value={max} onChange={(e) => setMax(e.target.value)}
                    className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 font-medium w-24">Quantity</label>
                  <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)}
                    className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" min="1" max="1000" />
                </div>
              </div>
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={allowDuplicates} onChange={(e) => setAllowDuplicates(e.target.checked)}
                    className="w-4 h-4 accent-cyan-500" />
                  <span className="text-gray-300">Allow duplicates</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={sortResults} onChange={(e) => setSortResults(e.target.checked)}
                    className="w-4 h-4 accent-cyan-500" />
                  <span className="text-gray-300">Sort results</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={generate} className="bg-cyan-600 hover:bg-cyan-500 text-white px-8">
                Generate
              </Button>
              {results.length > 0 && (
                <Button onClick={copyResults} variant="outline" className="border-[#2a2a2a] text-gray-300 hover:bg-[#2a2a2a]">
                  Copy
                </Button>
              )}
            </div>

            {results.length > 0 && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-lg font-semibold text-white">Results ({results.length} number{results.length > 1 ? 's' : ''})</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {results.map((num, i) => (
                    <span key={i} className="px-3 py-2 bg-cyan-600/20 border border-cyan-600/50 rounded-lg text-cyan-400 font-mono font-bold">
                      {num}
                    </span>
                  ))}
                </div>
                {results.length > 1 && (
                  <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                    <div className="p-3 bg-[#1a1a1a] rounded-lg">
                      <p className="text-gray-400">Sum</p>
                      <p className="text-white font-semibold">{results.reduce((a, b) => a + b, 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-[#1a1a1a] rounded-lg">
                      <p className="text-gray-400">Average</p>
                      <p className="text-white font-semibold">{(results.reduce((a, b) => a + b, 0) / results.length).toFixed(2)}</p>
                    </div>
                    <div className="p-3 bg-[#1a1a1a] rounded-lg">
                      <p className="text-gray-400">Range</p>
                      <p className="text-white font-semibold">{Math.min(...results)} - {Math.max(...results)}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">About Random Numbers</h2>
              <p className="text-gray-400 leading-relaxed">
                Random number generators (RNGs) are used in many applications including statistical sampling, 
                computer simulations, cryptography, and games. This generator uses a pseudorandom number 
                generator (PRNG) algorithm suitable for general purposes.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Common Uses</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {['Lottery & Raffles', 'Statistical Sampling', 'Game Development', 'Password Generation', 'Decision Making', 'Testing & QA'].map((use, i) => (
                  <div key={i} className="p-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg">
                    <p className="text-white">{use}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Math Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Scientific', path: '/scientific-calculator' }, { name: 'Percentage', path: '/percentage-calculator' }, { name: 'Standard Deviation', path: '/standard-deviation-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RandomNumberGeneratorPage;
